import { useRef } from "react"
import { Household, NextPageParams, PaginationMetadata } from "@/schema"
import { useInfiniteQuery, useQuery } from "@tanstack/react-query"

import { isPromptInProgress } from "@/lib/prompt-status"
import { useApiContext } from "@/hooks/api/use-api-context"
import { useBookId } from "@/hooks/use-book-id"

import {
  fetchFamilyMembers,
  fetchHousehold,
  fetchHouseholdPersonDataEnrichment,
  fetchHouseholds,
  fetchHouseholdSuggestedFields,
  fetchHouseholdType,
  fetchRecentHouseholds,
  HouseholdsResponse,
} from "./api"

const ENRICHMENT_POLL_INITIAL_INTERVAL = 5_000
const ENRICHMENT_POLL_MAX_INTERVAL = 60_000
const ENRICHMENT_POLL_MAX_ATTEMPTS = 6

export function useFetchHouseholds(
  searchTerm?: string,
  householdType?: "Prospect" | "Client" | "prospect" | "client" | null,
  bookIdOverride?: string,
  scope?: "company"
) {
  const urlBookId = useBookId()
  const bookId = bookIdOverride ?? urlBookId
  const getContext = useApiContext(bookIdOverride)

  return useInfiniteQuery<
    HouseholdsResponse,
    Error,
    { households: Household[]; pagination: PaginationMetadata },
    (string | null | undefined)[],
    NextPageParams | undefined
  >({
    queryKey: ["households", searchTerm, householdType, bookId, scope],
    queryFn: async ({ pageParam }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchHouseholds(context, searchTerm, householdType, pageParam, true, scope)
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.pagination.next_page_params ?? undefined,
    select: (data) => ({
      households: data.pages.flatMap((page) => page.households),
      pagination: data.pages[data.pages.length - 1]?.pagination,
    }),
  })
}

export function useFetchHousehold(id: string, options?: { enabled?: boolean; bookIdOverride?: string }) {
  const getContext = useApiContext(options?.bookIdOverride)
  return useQuery({
    queryKey: ["household", id],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchHousehold(context, id)
    },
    enabled: options?.enabled !== undefined ? options.enabled : !!id,
    refetchInterval: (query) => {
      const household = query.state.data
      if (!household) return false
      return isPromptInProgress(household, "Prompt::HouseholdSummary") ? 3000 : false
    },
    staleTime: 0,
    ...options,
  })
}

export function useFetchHouseholdType(id: string, options?: { enabled?: boolean }) {
  const getContext = useApiContext()
  return useQuery({
    queryKey: ["household", id, "type"],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchHouseholdType(context, id)
    },
    enabled: options?.enabled !== undefined ? options.enabled : !!id,
  })
}

export function useFetchRecentHouseholds() {
  const getContext = useApiContext()
  const bookId = useBookId()
  return useQuery({
    queryKey: [bookId, "households", "recent"],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchRecentHouseholds(context)
    },
  })
}

export function useFetchHouseholdSuggestedFields(householdId: string, options?: { enabled?: boolean }) {
  const getContext = useApiContext()
  return useQuery({
    queryKey: ["household", householdId, "suggested-fields"],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchHouseholdSuggestedFields(context, householdId)
    },
    enabled: options?.enabled !== undefined ? options.enabled : !!householdId,
  })
}

export function useFetchHouseholdPersonDataEnrichment(householdId: string, options?: { enabled?: boolean }) {
  const getContext = useApiContext()
  const pollState = useRef({
    householdId,
    attempts: 0,
    lastDataUpdatedAt: 0,
    lastErrorUpdatedAt: 0,
    interval: ENRICHMENT_POLL_INITIAL_INTERVAL,
  })

  const query = useQuery({
    queryKey: ["household", householdId, "person-data-enrichment"],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchHouseholdPersonDataEnrichment(context, householdId)
    },
    enabled: options?.enabled !== undefined ? options.enabled : !!householdId,
    refetchInterval: (query) => {
      if (pollState.current.householdId !== householdId) {
        pollState.current = {
          householdId,
          attempts: 0,
          lastDataUpdatedAt: 0,
          lastErrorUpdatedAt: 0,
          interval: ENRICHMENT_POLL_INITIAL_INTERVAL,
        }
      }

      const enrichment = query.state.data?.person_data_enrichment
      if (!enrichment) return false

      const hasSummary = Boolean(enrichment.ai_summary)
      const isFailed = enrichment.status === "failed"

      if (hasSummary || isFailed) {
        pollState.current.attempts = 0
        pollState.current.lastDataUpdatedAt = 0
        pollState.current.lastErrorUpdatedAt = 0
        return false
      }

      const hasNewResult =
        pollState.current.lastDataUpdatedAt !== query.state.dataUpdatedAt ||
        pollState.current.lastErrorUpdatedAt !== query.state.errorUpdatedAt

      if (!hasNewResult) {
        return pollState.current.interval
      }

      if (pollState.current.attempts >= ENRICHMENT_POLL_MAX_ATTEMPTS) {
        return false
      }

      pollState.current.lastDataUpdatedAt = query.state.dataUpdatedAt
      pollState.current.lastErrorUpdatedAt = query.state.errorUpdatedAt
      pollState.current.interval = Math.min(
        ENRICHMENT_POLL_INITIAL_INTERVAL * 2 ** pollState.current.attempts,
        ENRICHMENT_POLL_MAX_INTERVAL
      )
      pollState.current.attempts += 1

      return pollState.current.interval
    },
  })

  const enrichment = query.data?.person_data_enrichment
  const isPollingExhausted =
    Boolean(enrichment) &&
    !enrichment?.ai_summary &&
    enrichment?.status !== "failed" &&
    pollState.current.attempts >= ENRICHMENT_POLL_MAX_ATTEMPTS &&
    (pollState.current.lastDataUpdatedAt !== query.dataUpdatedAt ||
      pollState.current.lastErrorUpdatedAt !== query.errorUpdatedAt)

  return { ...query, isPollingExhausted }
}

const CREATED_POLL_MAX = 5

export function useFetchFamilyMembers(householdId: string, options?: { enabled?: boolean }) {
  const getContext = useApiContext()
  const createdPollCount = useRef(0)

  return useQuery({
    queryKey: ["household", householdId, "family-members"],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchFamilyMembers(context, householdId)
    },
    enabled: options?.enabled !== undefined ? options.enabled : !!householdId,
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data?.family_members?.length) return false

      const hasPending = data.family_members.some((m) => m.status === "pending")
      if (hasPending) {
        createdPollCount.current = 0
        return 5000
      }

      const hasCreated = data.family_members.some((m) => m.status === "created")
      if (hasCreated && createdPollCount.current < CREATED_POLL_MAX) {
        createdPollCount.current += 1
        return 5000
      }

      return false
    },
  })
}
